import { Sidebar, type SidebarFooterConfig, type SidebarSection } from '@deepdive-ui';
import { useMemo } from 'react';

import { NAV_ITEMS, navSectionsFor, type AppView } from '@/app/router/registry';
import { useSignOut } from '@/features/auth/useSignOut';
import { bootstrapApi, sourcedArg } from '@/features/data/bootstrap.api';
import { useDataSource } from '@/features/data/DataSourceContext';
import { canInvite } from '@/features/settings/invite';

export type SidebarContainerProps = {
  /** Which product's sections to draw — the page's (`viewOf(route)`). */
  view?: AppView;
  activeItemId?: string;
  /** Nav items whose pages exist in this tree (live hides pages not yet integrated). */
  visibleItemIds: ReadonlySet<string>;
  /** Omitted, the menu has no Invite member — an executive's (`EXECUTIVE_USERS`). */
  onInviteMember?: () => void;
  profilePhotoUrl?: string;
};

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

export function SidebarContainer({
  view = 'analyst',
  activeItemId,
  visibleItemIds,
  onInviteMember,
  profilePhotoUrl,
}: SidebarContainerProps) {
  const { source, bootstrap } = useDataSource();
  const { data: credits } = bootstrapApi.useCreditsQuery(sourcedArg(source));
  const { signOut } = useSignOut();

  const sections = useMemo<SidebarSection[]>(
    () =>
      navSectionsFor(view)
        .map((section) => ({
          id: section.id,
          label: section.label,
          items: NAV_ITEMS.filter(
            (item) => item.section === section.id && visibleItemIds.has(item.id),
          ).map((item) => ({
            id: item.id,
            label: item.label,
            icon: item.icon,
            href: item.href,
            badge: item.badge,
          })),
        }))
        .filter((section) => section.items.length > 0),
    [view, visibleItemIds],
  );

  const footer = useMemo<SidebarFooterConfig>(() => {
    const { user } = bootstrap.session;
    return {
      name: bootstrap.workspace.name,
      avatarSrc: bootstrap.workspace.avatarUrl ?? '',
      credits: credits ? { left: credits.remaining, total: credits.total } : undefined,
      profile: {
        name: user.name,
        email: user.email,
        initials: initialsOf(user.name),
        photoSrc: profilePhotoUrl ?? user.photoUrl,
      },
      menu: [
        ...(visibleItemIds.has('settings')
          ? [{ id: 'settings', label: 'Settings', icon: 'settings' as const, href: '/settings' }]
          : []),
        ...(onInviteMember && canInvite(user.role)
          ? [
              {
                id: 'invite-member',
                label: 'Invite member',
                icon: 'invite' as const,
                onClick: onInviteMember,
              },
            ]
          : []),
        { id: 'faq', label: 'FAQ', icon: 'help' as const },
      ],
      // The live tree signs out against the API; the sample-data tree has no session.
      // No confirmation step: none is designed yet (docs/design/open-questions.md #12).
      onSignOut: source === 'live' ? () => void signOut() : () => {},
    };
  }, [bootstrap, credits, onInviteMember, profilePhotoUrl, signOut, source, visibleItemIds]);

  return <Sidebar sections={sections} activeItemId={activeItemId} footer={footer} />;
}
